/**
 * Block break particles — small colored cubes burst out of the broken
 * block with gravity, Minecraft style.
 */

import * as THREE from "three";
import { getBlockColor } from "../ui/hotbar";

type Particle = {
  mesh: THREE.Mesh;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  maxLife: number;
};

const GRAVITY = 18;
const PARTICLES_PER_BURST = 14;

export type BlockParticles = {
  spawnBreak: (wx: number, wy: number, wz: number, blockId: number) => void;
  update: (dt: number) => void;
  dispose: () => void;
};

export function createBlockParticles(scene: THREE.Scene): BlockParticles {
  const particles: Particle[] = [];
  const geometry = new THREE.BoxGeometry(0.08, 0.08, 0.08);
  const materialCache = new Map<number, THREE.MeshBasicMaterial>();

  function materialFor(blockId: number): THREE.MeshBasicMaterial {
    let material = materialCache.get(blockId);
    if (!material) {
      material = new THREE.MeshBasicMaterial({
        color: new THREE.Color(getBlockColor(blockId)),
        transparent: true,
      });
      materialCache.set(blockId, material);
    }
    return material;
  }

  function spawnBreak(wx: number, wy: number, wz: number, blockId: number) {
    const material = materialFor(blockId);
    for (let i = 0; i < PARTICLES_PER_BURST; i++) {
      const mesh = new THREE.Mesh(geometry, material.clone());
      mesh.position.set(
        wx + 0.2 + Math.random() * 0.6,
        wy + 0.2 + Math.random() * 0.6,
        wz + 0.2 + Math.random() * 0.6
      );
      scene.add(mesh);

      const angle = Math.random() * Math.PI * 2;
      const speed = 1.5 + Math.random() * 2;
      particles.push({
        mesh,
        vx: Math.cos(angle) * speed,
        vy: 2 + Math.random() * 3,
        vz: Math.sin(angle) * speed,
        life: 0,
        maxLife: 0.45 + Math.random() * 0.3,
      });
    }
  }

  function update(dt: number) {
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.life += dt;
      if (p.life >= p.maxLife) {
        scene.remove(p.mesh);
        (p.mesh.material as THREE.Material).dispose();
        particles.splice(i, 1);
        continue;
      }
      p.vy -= GRAVITY * dt;
      p.mesh.position.x += p.vx * dt;
      p.mesh.position.y += p.vy * dt;
      p.mesh.position.z += p.vz * dt;
      p.mesh.rotation.x += dt * 6;
      p.mesh.rotation.y += dt * 4;
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = 1 - p.life / p.maxLife;
    }
  }

  function dispose() {
    for (const p of particles) {
      scene.remove(p.mesh);
      (p.mesh.material as THREE.Material).dispose();
    }
    particles.length = 0;
    geometry.dispose();
    for (const material of materialCache.values()) material.dispose();
    materialCache.clear();
  }

  return { spawnBreak, update, dispose };
}
